/**
 * @file api/members.js
 * @location src/integrations/supabase/api/members.js
 * @role 팀 생성 · 멤버/초대 RPC 래퍼.
 *
 *  createTeam/acceptInvite 는 jsonb({team_id, member_id}) 반환 — 온보딩 전용 경로라 별도 처리.
 *  나머지(invite/edit/delete) 는 Member row 반환 → 공용 callRpc 경유.
 */
import { supabase } from '../client.js';
import { rowToMember } from '../mappers.js';
import { callRpc, callRpcId } from './_call.js';

const callMember = (fn, args) => callRpc(fn, args, rowToMember);

/** 온보딩: 새 팀 생성(본인=오너 매니저). → { team_id, member_id } */
export async function createTeam(teamName, memberName) {
  const { data, error } = await supabase.rpc('rpc_create_team', {
    p_team_name: teamName ?? '', p_member_name: memberName ?? '',
  });
  if (error) throw error;
  return data;
}

/** 초대 수락: 코드로 pending 자리에 연결. → { team_id, member_id } */
export async function acceptInvite({ code, name, email, mainCases }) {
  const { data, error } = await supabase.rpc('rpc_accept_invite', {
    p_code: code, p_name: name,
    p_email: email ?? null, p_main_cases: mainCases ?? null,
  });
  if (error) throw error;
  return data;
}

/** 매니저: pending 자리 생성(코드 발급). → Member(pending) */
export const createInvite  = (name) => callMember('rpc_create_invite', { p_name: name ?? '' });
export const reissueInvite = (id)   => callMember('rpc_reissue_invite', { p_member_id: id });

export const editMember = (id, c) => callMember('rpc_edit_member', {
  p_member_id: id, p_name: c.name ?? null, p_email: c.email ?? null,
  p_main_cases: c.mainCases ?? null,
});

/** 본인 프로필(이름) 자가 수정 — 매니저/팀원 누구나 자기 자신만. → Member */
export const updateProfile = (name) => callMember('rpc_update_profile', { p_name: name ?? '' });

export const deleteMember = (id) => callRpcId('rpc_delete_member', { p_member_id: id }, id);

/**
 * 매니저 권한 이양 — 서버가 두 멤버의 is_manager 를 swap.
 * 단일 reduce 가 두 멤버 동시 갱신 못 함 → 호출 측이 refetchWorkspace 로 보정.
 *
 * @param {string} targetMemberId
 * @returns {Promise<{ newManagerId: string }>}
 */
export async function transferManager(targetMemberId) {
  await callRpcId('rpc_transfer_manager', { p_target_member_id: targetMemberId }, targetMemberId);
  return { newManagerId: targetMemberId };
}
